import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import { IrisBannerType } from './banner-type.enum';

const ICON_COLOR_TOKEN_BY_TYPE: Record<IrisBannerType, string> = {
  [IrisBannerType.INFO]: '$color-dec-turquoise-100',
  [IrisBannerType.WARNING]: '$color-neutral-default',
  [IrisBannerType.SUCCESS]: '$color-dec-green-60',
  [IrisBannerType.DISABLED]: '$color-neutral-60',
};

@Component({
  selector: 'iris-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IrisIconComponent,
    IrisIconButtonModule
  ],
  host: {
    class: 'iris-banner-host',
    '[attr.role]': "'region'",
    '[attr.aria-label]': 'bannerAriaLabel() ?? null',
    '[attr.aria-labelledby]': 'bannerLabelledBy() ?? null'
  }
})
export class IrisBannerComponent {
  readonly bannerType = input<IrisBannerType>(IrisBannerType.INFO);
  readonly headerText = input<string | undefined>(undefined);

  readonly iconName = input<string | undefined>(undefined);
  readonly showDismiss = input(true);
  readonly dismissAriaLabel = input('Dismiss notification');
  readonly bannerAriaLabel = input<string | undefined>(undefined);
  readonly bannerLabelledBy = input<string | undefined>(undefined);

  readonly dismiss = output();

  readonly dismissed = signal(false);

  readonly irisBannerType = IrisBannerType;
  readonly irisIconButtonTypes = IrisIconButtonTypes;
  readonly buttonTypeAttribute = IrisButtonTypeAttributeOptions;
  readonly iconSizes = IconSizes;

  readonly hasIcon = computed(() => {
    const name = this.iconName();
    return typeof name === 'string' && name.trim().length > 0;
  });

  readonly iconColorToken = computed(() => ICON_COLOR_TOKEN_BY_TYPE[this.bannerType()]);

  onDismiss(): void {
    this.dismissed.set(true);
    this.dismiss.emit();
  }
}
